import { useEffect, useMemo, useState } from 'react';
import {
  discardSleep,
  endSleep,
  getActiveSleepSession,
  startSleep,
  updateActiveSleep,
} from '../services/activeSleepService';

const useActiveSleep = (childId) => {
  const [activeSleep, setActiveSleep] = useState(null);

  useEffect(() => {
    if (typeof console === 'undefined') {
      return;
    }

    console.info('[sleep] useActiveSleep input', {
      childId,
      activeSleepSessionPath: childId ? `activeSleepSessions/${childId}_activeSleep` : null,
    });
  }, [childId]);

  useEffect(() => {
    if (!childId) {
      setActiveSleep(null);
      return undefined;
    }

    let isMounted = true;

    const refresh = async () => {
      const session = await getActiveSleepSession(childId);

      if (!isMounted) {
        return;
      }

      setActiveSleep(session);

      if (typeof console !== 'undefined') {
        console.info('[sleep] useActiveSleep refresh result', {
          childId,
          session: session
            ? {
              id: session.id,
              childId: session.childId,
              startTime: session.startTime,
              endTime: session.endTime,
              status: session.status,
            }
            : null,
        });
      }
    };

    refresh();

    const handleRefresh = (event) => {
      const eventChildId = event?.detail?.childId;
      if (eventChildId && eventChildId !== childId) {
        return;
      }
      refresh();
    };

    const handleStorage = (event) => {
      if (typeof event?.key !== 'string' || !event.key.includes(`:${childId}_`)) {
        return;
      }
      refresh();
    };

    window.addEventListener('captureez:active-sleep-refresh', handleRefresh);
    window.addEventListener('storage', handleStorage);

    return () => {
      isMounted = false;
      window.removeEventListener('captureez:active-sleep-refresh', handleRefresh);
      window.removeEventListener('storage', handleStorage);
    };
  }, [childId]);

  const hasActiveSleep = Boolean(activeSleep);

  useEffect(() => {
    if (typeof console === 'undefined') {
      return;
    }

    console.info('[sleep] useActiveSleep return value', {
      childId,
      hasActiveSleep,
      activeSleep: activeSleep
        ? {
          id: activeSleep.id,
          childId: activeSleep.childId,
          startTime: activeSleep.startTime,
          endTime: activeSleep.endTime,
          status: activeSleep.status,
        }
        : null,
    });
  }, [activeSleep, childId, hasActiveSleep]);

  const api = useMemo(() => ({
    activeSleep,
    hasActiveSleep,
    startSleep: (type = 'night', updates = {}) => startSleep(childId, type, updates),
    resumeSleep: () => getActiveSleepSession(childId),
    endSleep: (type = 'night', updates = {}) => endSleep(childId, type, updates),
    updateActiveSleep: (type = 'night', updates = {}) => updateActiveSleep(childId, type, updates),
    discardSleep: (type = 'night') => discardSleep(childId, type),
  }), [activeSleep, childId, hasActiveSleep]);

  return api;
};

export default useActiveSleep;
